import React from 'react';
import {MRT_ColumnDef} from 'material-react-table';
import {Box, Chip, IconButton, Stack, Tooltip, Typography} from '@mui/material';
import {Archive, ChevronRight, ExpandMore, MoreVert} from '@mui/icons-material';
import moment from 'moment';
import {ResourceType} from '../../../../@types/resource-type';
import {ComponentMappedSource} from '../../../../@types/component';
import {ExplorerDatasetType, ExplorerTableConfig, ExtendedTreeViewItem} from './explorer-mrt-types';
import {getResourceConfig} from './explorer-table-config';
import authService from '../../../../services/auth.service';

/**
 * Helper function to get the appropriate color for IRP status chips
 */
export const getIRPStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
        case 'DRAFT':
            return 'default'; // gray
        case 'QUEUED':
            return 'warning'; // yellow/orange
        case 'RUNNING':
            return 'info'; // blue
        case 'COMPLETED':
            return 'success'; // green
        case 'ERROR':
            return 'error'; // red
        case 'CANCELLED':
            return 'secondary'; // gray
        case 'ARCHIVED':
            return 'secondary'; // purple/gray
        default:
            return 'default';
    }
};

/**
 * Helper function to get tooltip text explaining IRP status.
 * For ERROR status, prefers the specific batch job failure reason when available,
 * falling back to a generic message otherwise.
 */
export const getIRPStatusTooltip = (status: string, errorMessage?: string): string => {
    switch (status) {
        case 'DRAFT':
            return 'Initial state - IRP can be edited, run, or deleted';
        case 'QUEUED':
            return 'Ready for processing - waiting to be picked up by the system';
        case 'RUNNING':
            return 'Currently being processed - analyzing reports and creating mappings';
        case 'COMPLETED':
            return 'Processing completed successfully - results are available';
        case 'ERROR':
            return errorMessage || 'Processing failed - can be retried or deleted';
        case 'CANCELLED':
            return 'Processing was cancelled by user';
        case 'ARCHIVED':
            return 'IRP has been archived and is no longer active';
        default:
            return 'Unknown status';
    }
};

interface ColumnConfigOptions {
    currentConfig: ExplorerTableConfig;
    matchingIds: Set<string>;
    handleActionMenu: (event: React.MouseEvent<HTMLElement>, item: ExtendedTreeViewItem) => void;
    getResourceIcon: (type: ResourceType) => JSX.Element;
}

// Occurrence Report specific columns
const getOccurrenceReportColumns = (
    options: ColumnConfigOptions
): MRT_ColumnDef<ExtendedTreeViewItem>[] => {
    const { handleActionMenu } = options;
    
    return [
        {
            id: 'id',
            header: 'ID',
            size: 100,
            minSize: 80,
            maxSize: 120,
            Cell: ({ row }): JSX.Element => {
                const item = row.original;
                return (
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
                        {item.object?.uniqueId || item.id || 'N/A'}
                    </Typography>
                );
            },
        },
        {
            id: 'datetime',
            header: 'Date/Time',
            size: 150,
            minSize: 120,
            maxSize: 180,
            Cell: ({ row }): JSX.Element => {
                const item = row.original;
                const datetime = item.object?.dateTime || item.object?.datetime;
                return (
                    <Typography variant="body2">
                        {datetime ? moment.utc(datetime).format('YYYY-MM-DD HH:mm') + ' UTC' : 'N/A'}
                    </Typography>
                );
            },
        },
        {
            id: 'location',
            header: 'Location',
            size: 200,
            minSize: 150,
            grow: true,
            Cell: ({ row }): JSX.Element => {
                const item = row.original;
                const location = item.object?.facilityName || item.object?.location || '';
                return (
                    <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {location || 'N/A'}
                    </Typography>
                );
            },
        },
        {
            id: 'source',
            header: 'Source',
            size: 120,
            minSize: 100,
            maxSize: 150,
            Cell: ({ row }): JSX.Element => {
                const item = row.original;
                const sourceName = item.object?.sourceName;

                return (
                    <Box>
                        {sourceName && (
                            <Chip
                                label={sourceName}
                                size="small"
                                color="info"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '10px' }}
                            />
                        )}
                    </Box>
                );
            },
        },
        {
            id: 'descriptors',
            header: 'Descriptors',
            size: 400,
            minSize: 250,
            Cell: ({ row }): JSX.Element => {
                const item = row.original;
                const descriptorTerms = item.object?.descriptorTerms || [];

                return (
                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        {descriptorTerms.map((term: any, index: number) => {
                            // Determine chip color based on descriptor set - using extended chart-style palette
                            const getDescriptorColor = (descriptorName: string | undefined): string => {
                                if (!descriptorName) return '#2196F3'; // default blue

                                // Extended color palette similar to chart libraries (20 distinct colors)
                                const chartColors = [
                                    '#2196F3', // blue
                                    '#4CAF50', // green
                                    '#FF9800', // orange
                                    '#9C27B0', // purple
                                    '#F44336', // red
                                    '#00BCD4', // cyan
                                    '#FFEB3B', // yellow
                                    '#E91E63', // pink
                                    '#3F51B5', // indigo
                                    '#8BC34A', // light green
                                    '#FF5722', // deep orange
                                    '#673AB7', // deep purple
                                    '#009688', // teal
                                    '#FFC107', // amber
                                    '#795548', // brown
                                    '#607D8B', // blue grey
                                    '#CDDC39', // lime
                                    '#00E676', // bright green
                                    '#FF6F00', // dark orange
                                    '#6A1B9A', // dark purple
                                ];

                                // Simple hash function
                                let hash = 0;
                                for (let i = 0; i < descriptorName.length; i++) {
                                    hash = ((hash << 5) - hash) + descriptorName.charCodeAt(i);
                                    hash = hash & hash; // Convert to 32-bit integer
                                }

                                return chartColors[Math.abs(hash) % chartColors.length];
                            };

                            const tooltipContent = (
                                <Box sx={{ maxWidth: 800 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                        {term.descriptorName || 'Descriptor'} {term.descriptorVersion ? `(v${term.descriptorVersion})` : ''}: {term.term}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        {term.definition || 'No definition available'}
                                    </Typography>
                                    {term.confidenceScore !== undefined && (
                                        <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                                            <strong>Confidence:</strong> {(term.confidenceScore * 100).toFixed(0)}%
                                        </Typography>
                                    )}
                                    {term.explanation && (
                                        <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                                            <strong>Explanation:</strong> {term.explanation}
                                        </Typography>
                                    )}
                                    {term.supportingPhrases && term.supportingPhrases.length > 0 && (
                                        <>
                                            <Typography variant="caption" sx={{ display: 'block', mt: 1, mb: 0.5 }}>
                                                <strong>Supporting phrases:</strong>
                                            </Typography>
                                            <Box component="ul" sx={{ mt: 0, mb: 0, pl: 2 }}>
                                                {term.supportingPhrases.map((phrase: string, idx: number) => (
                                                    <Box component="li" key={idx}>
                                                        <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                                                            "{phrase}"
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </>
                                    )}
                                    {term.llmModel && (
                                        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                                            <strong>Model:</strong> {term.llmModel}
                                        </Typography>
                                    )}
                                </Box>
                            );

                            return (
                                <Tooltip
                                    key={index}
                                    title={tooltipContent}
                                    placement="top"
                                    arrow
                                    slotProps={{
                                        popper: {
                                            sx: {
                                                '& .MuiTooltip-tooltip': {
                                                    maxWidth: 800
                                                }
                                            }
                                        }
                                    }}
                                >
                                    <Chip
                                        label={`${term.term}${term.descriptorVersion && term.descriptorVersion > 1 ? ` v${term.descriptorVersion}` : ''}`}
                                        size="small"
                                        sx={{
                                            height: 20,
                                            fontSize: '10px',
                                            cursor: 'help',
                                            backgroundColor: getDescriptorColor(term.descriptorName),
                                            color: '#fff',
                                            fontWeight: 500,
                                            '&:hover': {
                                                backgroundColor: getDescriptorColor(term.descriptorName),
                                                filter: 'brightness(0.9)'
                                            }
                                        }}
                                    />
                                </Tooltip>
                            );
                        })}
                    </Stack>
                );
            },
        },
        {
            id: 'actions',
            header: '',
            size: 30,
            enableColumnActions: false,
            enableSorting: false,
            enableResizing: false,
            Cell: ({ row }): JSX.Element => (
                <IconButton
                    size="small"
                    onClick={(e): void => handleActionMenu(e, row.original)}
                >
                    <MoreVert fontSize="small" />
                </IconButton>
            ),
        },
    ];
};

// Default columns for all other resource types
/**
 * What a library row says about a component's mapping. Deliberately modest: how many report
 * sources it has been run against and when, which the list gets for one query per page. Whether
 * those runs are still current needs a counted pass over each source, which the Mapping tab pays
 * for one source at a time.
 */
function componentMappingLabel(component: {sourcesRunAgainst?: number} | undefined): string {
    const sources = component?.sourcesRunAgainst ?? 0;
    if (sources === 0) {
        return 'Not mapped';
    }
    return `${sources} source${sources === 1 ? '' : 's'}`;
}

function componentMappingTooltip(
    component: {sourcesRunAgainst?: number; lastRunAt?: string} | undefined,
): string {
    const sources = component?.sourcesRunAgainst ?? 0;
    if (sources === 0) {
        return 'This version has never been mapped against a report source';
    }
    const when = component?.lastRunAt
        ? `; last run ${moment(component.lastRunAt).format('D MMM YYYY')}`
        : '';
    return `Mapped against ${sources} report source${sources === 1 ? '' : 's'}${when}.`
        + ' Open the component to see whether those mappings are still current.';
}

const getDefaultColumns = (
    options: ColumnConfigOptions
): MRT_ColumnDef<ExtendedTreeViewItem>[] => {
    const {
        currentConfig,
        matchingIds,
        handleActionMenu,
        getResourceIcon
    } = options;
    
    return [
        {
            id: 'name',
            accessorKey: 'name',
            header: 'Name',
            size: 300,
            minSize: 200,
            grow: true,
            Cell: ({ row, cell }): JSX.Element => {
                const item = row.original;
                const hasChildren = item.hasChildren === true || (item.children && item.children.length > 0);
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            pl: `${row.depth * 24}px`,
                        }}
                    >
                        <Box sx={{ width: 28, display: 'flex', alignItems: 'center' }}>
                            {hasChildren && (
                                <IconButton
                                    size="small"
                                    onClick={(e): void => {
                                        e.stopPropagation();
                                        row.toggleExpanded();
                                    }}
                                    sx={{ p: 0.5 }}
                                >
                                    {row.getIsExpanded() ? <ExpandMore /> : <ChevronRight />}
                                </IconButton>
                            )}
                        </Box>
                        {getResourceIcon(row.original.type)}
                        <Tooltip
                            title={item.type === ResourceType.GROUPS ? `Group ID: ${item.object?.id || item.id.replace(/^group-/, '')}` : ''}
                            placement="top"
                            arrow
                            disableHoverListener={item.type !== ResourceType.GROUPS}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    ml: 1,
                                    fontWeight: matchingIds.has(row.original.id) ? 'bold' : 'normal',
                                    color: matchingIds.has(row.original.id) ? 'primary.main' : 'inherit',
                                }}
                            >
                                {row.original.type === ResourceType.COMPONENT && row.original.object?.uniqueId && (
                                    <Typography
                                        component="span"
                                        variant="body2"
                                        sx={{ color: 'text.secondary', mr: 0.5 }}
                                    >
                                        {row.original.object.uniqueId}
                                    </Typography>
                                )}
                                {cell.getValue<string>()}
                                {row.original.type === ResourceType.SAFETY_MODELS &&
                                 currentConfig.showSequenceCount && (
                                    <Typography
                                        component="span"
                                        variant="caption"
                                        sx={{ ml: 1, color: 'text.secondary' }}
                                    >
                                        ({row.original.numberEventSequences || 0} {row.original.numberEventSequences === 1 ? 'sequence' : 'sequences'})
                                    </Typography>
                                )}
                            </Typography>
                        </Tooltip>
                    </Box>
                );
            },
        },
        {
            id: 'typeAndDetails',
            header: 'Type',
            size: 150,
            minSize: 100,
            maxSize: 200,
            grow: false,
            Cell: ({ row }): JSX.Element => {
                const item = row.original;
                const typeToUse = item.type;
                const resourceConfig = getResourceConfig(typeToUse, currentConfig);
                const typeName = resourceConfig?.displayName || 'Unknown';
                const chipColor = resourceConfig?.chipColor || 'default';
                
                return (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                            label={typeName}
                            size="small"
                            color={chipColor}
                            variant="outlined"
                        />
                        
                        {item.type === ResourceType.GROUPS && 
                         typeof item.groupRole === 'string' &&
                         currentConfig.showGroupRole && (
                            <Chip
                                label={item.groupRole ? item.groupRole.toUpperCase() : 'NOT MEMBER'}
                                size="small"
                                variant="outlined"
                                color={item.groupRole ? 'default' : 'default'}
                                sx={{ 
                                    height: 20, 
                                    fontSize: '10px',
                                    opacity: item.groupRole ? 1 : 0.6,
                                    fontStyle: item.groupRole ? 'normal' : 'italic'
                                }}
                            />
                        )}
                        
                        {item.type === ResourceType.USERS &&
                         item.object?.superUser && (
                            <Chip
                                label="SUPERUSER"
                                size="small"
                                color="error"
                                variant="filled"
                                sx={{ height: 20, fontSize: '10px' }}
                            />
                        )}
                        
                        {item.type === ResourceType.OCCURRENCE_REPORTS &&
                         item.object?.sourceName && (
                            <Chip
                                label={item.object.sourceName}
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '10px' }}
                            />
                        )}

                        {/*
                          * What a component has been run against, not whether it is still current:
                          * the live status costs a counted pass over a report source per pair, and
                          * a list cannot pay that per row. The Mapping tab answers currency, one
                          * source at a time.
                          */}
                        {item.type === ResourceType.COMPONENT && (
                            <Tooltip title={componentMappingTooltip(item.object)}>
                                <Chip
                                    label={componentMappingLabel(item.object)}
                                    size="small"
                                    variant="outlined"
                                    color={item.object?.sourcesRunAgainst ? 'default' : 'warning'}
                                    sx={{ height: 20, fontSize: '10px' }}
                                />
                            </Tooltip>
                        )}
                    </Stack>
                );
            },
        },
        {
            id: 'actions',
            header: '',
            size: 30,
            enableColumnActions: false,
            enableSorting: false,
            enableResizing: false,
            Cell: ({ row }): JSX.Element => (
                <IconButton
                    size="small"
                    onClick={(e): void => handleActionMenu(e, row.original)}
                >
                    <MoreVert fontSize="small" />
                </IconButton>
            ),
        },
    ];
};

// Component Catalogue specific columns: every row is a published Component, so there is no need
// for a Type column -- what matters instead is the identity, when it was released, who owns it,
// and what it has been mapped against (component-solution-spec.md section 5).
const getComponentCatalogueColumns = (
    options: ColumnConfigOptions
): MRT_ColumnDef<ExtendedTreeViewItem>[] => {
    const { handleActionMenu } = options;

    return [
        {
            id: 'name',
            accessorKey: 'name',
            header: 'Name',
            size: 340,
            minSize: 220,
            grow: true,
            Cell: ({ row }): JSX.Element => {
                const component = row.original.object;
                return (
                    <Typography variant="body2">
                        {component?.uniqueId && (
                            <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
                                {component.uniqueId} -{' '}
                            </Typography>
                        )}
                        {component?.name}
                        {component?.version && (
                            <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
                                {' '}(v{component.version})
                            </Typography>
                        )}
                    </Typography>
                );
            },
        },
        {
            id: 'releaseDate',
            header: 'Release Date',
            size: 140,
            minSize: 120,
            maxSize: 160,
            Cell: ({ row }): JSX.Element => {
                // A release is a frozen snapshot: its own created timestamp is when it was taken.
                const created = row.original.object?.created;
                return (
                    <Typography variant="body2">
                        {created ? moment(created).format('D MMM YYYY') : 'N/A'}
                    </Typography>
                );
            },
        },
        {
            id: 'workingArea',
            header: 'Working Area',
            size: 200,
            minSize: 150,
            Cell: ({ row }): JSX.Element => {
                const groupName = row.original.object?.group?.name;
                return (
                    <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                        {groupName || 'N/A'}
                    </Typography>
                );
            },
        },
        {
            id: 'mappedSources',
            header: 'Mapped Sources',
            size: 260,
            minSize: 160,
            grow: true,
            Cell: ({ row }): JSX.Element => {
                const component = row.original.object;
                const mappedSources: ComponentMappedSource[] = component?.mappedSources || [];

                if (mappedSources.length === 0) {
                    return (
                        <Tooltip title="This version has never been mapped against a report source">
                            <Chip
                                label="Not mapped"
                                size="small"
                                variant="outlined"
                                color="warning"
                                sx={{ height: 20, fontSize: '10px' }}
                            />
                        </Tooltip>
                    );
                }

                return (
                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        {mappedSources.map((source) => (
                            <Tooltip
                                key={source.sourceName}
                                title={source.complete
                                    ? `Mapped; last run ${source.lastRunAt ? moment(source.lastRunAt).format('D MMM YYYY') : 'unknown'}`
                                    : 'Mapping in progress or not yet completed'}
                            >
                                <Chip
                                    label={source.sourceName}
                                    size="small"
                                    variant={source.complete ? 'filled' : 'outlined'}
                                    color={source.complete ? 'default' : 'warning'}
                                    sx={{ height: 20, fontSize: '10px' }}
                                />
                            </Tooltip>
                        ))}
                    </Stack>
                );
            },
        },
        {
            id: 'actions',
            header: '',
            size: 30,
            enableColumnActions: false,
            enableSorting: false,
            enableResizing: false,
            Cell: ({ row }): JSX.Element => (
                <IconButton
                    size="small"
                    onClick={(e): void => handleActionMenu(e, row.original)}
                >
                    <MoreVert fontSize="small" />
                </IconButton>
            ),
        },
    ];
};

// User specific columns: default columns (name, type) plus a Created column
const getUserColumns = (
    options: ColumnConfigOptions
): MRT_ColumnDef<ExtendedTreeViewItem>[] => {
    const defaultColumns = getDefaultColumns(options);

    const createdColumn: MRT_ColumnDef<ExtendedTreeViewItem> = {
        id: 'created',
        header: 'Created',
        size: 170,
        minSize: 140,
        maxSize: 220,
        enableSorting: true,
        accessorFn: (row): string => row.object?.created || '',
        Cell: ({ row }): JSX.Element => {
            const created = row.original.object?.created;
            return (
                <Typography variant="body2">
                    {created ? moment(created).format('YYYY-MM-DD HH:mm') : 'N/A'}
                </Typography>
            );
        },
    };

    // Insert the Created column just before the trailing actions column
    const actionsIndex = defaultColumns.findIndex((col): boolean => col.id === 'actions');
    if (actionsIndex === -1) {
        return [...defaultColumns, createdColumn];
    }
    return [
        ...defaultColumns.slice(0, actionsIndex),
        createdColumn,
        ...defaultColumns.slice(actionsIndex),
    ];
};

// Integrated Risk Picture specific columns
const getIRPColumns = (
    options: ColumnConfigOptions
): MRT_ColumnDef<ExtendedTreeViewItem>[] => {
    const { handleActionMenu, getResourceIcon } = options;

    return [
        {
            id: 'name',
            accessorKey: 'name',
            header: 'Name',
            size: 300,
            minSize: 200,
            grow: true,
            Cell: ({ row }): JSX.Element => {
                const item = row.original;

                // Group rows use the same expand-button pattern as getDefaultColumns
                if (item.type === ResourceType.GROUPS) {
                    const hasChildren = item.hasChildren === true || (item.children && item.children.length > 0);
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', pl: `${row.depth * 24}px` }}>
                            <Box sx={{ width: 28, display: 'flex', alignItems: 'center' }}>
                                {hasChildren && (
                                    <IconButton
                                        size="small"
                                        onClick={(e): void => {
                                            e.stopPropagation();
                                            row.toggleExpanded();
                                        }}
                                        sx={{ p: 0.5 }}
                                    >
                                        {row.getIsExpanded() ? <ExpandMore /> : <ChevronRight />}
                                    </IconButton>
                                )}
                            </Box>
                            {getResourceIcon(ResourceType.GROUPS)}
                            <Typography variant="body2" sx={{ ml: 1 }}>
                                {item.name}
                            </Typography>
                        </Box>
                    );
                }

                // IRP leaf rows: indented tooltip card
                const obj = item.object;
                const tooltipContent = (
                    <Box sx={{ p: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            {item.name}
                        </Typography>
                        <Stack spacing={0.5}>
                            <Typography variant="caption" display="block">
                                <strong>Safety Model:</strong> {obj?.simpleEventSequences?.[0]?.safetyModelName || obj?.safetyModelName || 'N/A'}
                            </Typography>
                            <Typography variant="caption" display="block">
                                <strong>Safety View:</strong> {obj?.simpleEventSequences?.[0]?.eventSequenceName || obj?.eventSequenceName || 'N/A'}
                            </Typography>
                            <Typography variant="caption" display="block">
                                <strong>Reports Evaluated:</strong> {obj?.totalReportsEvaluated !== undefined && obj?.totalReports !== undefined
                                    ? `${obj.totalReportsEvaluated.toLocaleString()} out of ${obj.totalReports.toLocaleString()}`
                                    : (obj?.totalReportsEvaluated?.toLocaleString() || '0')}
                            </Typography>
                            <Typography variant="caption" display="block">
                                <strong>Mappings Created:</strong> {obj?.totalMappingsCreated?.toLocaleString() || '0'}
                            </Typography>
                            <Typography variant="caption" display="block">
                                <strong>Created:</strong> {obj?.created ? moment(obj.created).format('YYYY-MM-DD HH:mm') : 'N/A'}
                            </Typography>
                            <Typography variant="caption" display="block">
                                <strong>Created By:</strong> {typeof obj?.createdBy === 'string' ? obj.createdBy : obj?.createdBy?.username || 'N/A'}
                            </Typography>
                            {obj?.completedAt && (
                                <Typography variant="caption" display="block">
                                    <strong>Completed:</strong> {moment(obj.completedAt).format('YYYY-MM-DD HH:mm')}
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                );

                const isArchived = obj?.archived === true;

                return (
                    <Tooltip title={tooltipContent} placement="right" arrow enterDelay={500} leaveDelay={200}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', pl: `${row.depth * 24}px` }}>
                            {getResourceIcon(ResourceType.INTEGRATED_RISK_PICTURES)}
                            {isArchived && <Archive fontSize="small" color="disabled" />}
                            <Typography
                                variant="body2"
                                noWrap
                                sx={{
                                    ...(isArchived && {
                                        textDecoration: 'line-through',
                                        color: 'text.disabled',
                                    }),
                                }}
                            >
                                {item.name}
                            </Typography>
                        </Box>
                    </Tooltip>
                );
            },
        },
        {
            id: 'status',
            header: 'Job Status',
            size: 140,
            minSize: 120,
            maxSize: 180,
            enableSorting: true,
            accessorFn: (row) => row.object?.status || 'UNKNOWN',
            Cell: ({ row }): JSX.Element => {
                const item = row.original;
                if (item.type === ResourceType.GROUPS) {
                    return (
                        <Chip
                            label="Group"
                            size="small"
                            color="default"
                            variant="outlined"
                            sx={{ cursor: 'default' }}
                        />
                    );
                }
                const status = item.object?.status || 'UNKNOWN';
                const progressPercent = item.object?.progressPercent;

                // For RUNNING status, show progress in the label
                const label = status === 'RUNNING' && progressPercent !== undefined
                    ? `${status} (${progressPercent}%)`
                    : status;

                return (
                    <Tooltip title={getIRPStatusTooltip(status, item.object?.errorMessage)} arrow>
                        <Chip
                            label={label}
                            size="small"
                            color={getIRPStatusColor(status)}
                            variant="outlined"
                            clickable={false}
                            sx={{ cursor: 'default' }}
                        />
                    </Tooltip>
                );
            },
        },
        {
            id: 'actions',
            header: '',
            size: 50,
            minSize: 50,
            maxSize: 50,
            enableSorting: false,
            enableColumnFilter: false,
            Cell: ({ row }): JSX.Element => (
                <IconButton
                    size="small"
                    onClick={(e): void => handleActionMenu(e, row.original)}
                    sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
                >
                    <MoreVert fontSize="small" />
                </IconButton>
            ),
        },
    ];
};

// Foundry Event specific columns: uniqueId, datetime, source (read-only, no actions)
/** The Safety Recommendations dataset lists sources: how much is held from each, and its sync state. */
const getSafetyRecommendationSourceColumns = (
    options: ColumnConfigOptions
): MRT_ColumnDef<ExtendedTreeViewItem>[] => {
    const { handleActionMenu } = options;
    return [
        {
            id: 'name',
            header: 'Source',
            size: 200,
            minSize: 140,
            grow: true,
            Cell: ({ row }): JSX.Element => (
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.original.name}</Typography>
            ),
        },
        {
            id: 'recommendationCount',
            header: 'Recommendations',
            size: 150,
            minSize: 120,
            maxSize: 180,
            Cell: ({ row }): JSX.Element => {
                const held = row.original.object?.recommendationCount ?? 0;
                const indexed = row.original.object?.indexedCount;
                return (
                    <Box>
                        <Typography variant="body2">{held.toLocaleString()}</Typography>
                        {/* Only worth saying when the index is behind: equal numbers are the normal state. */}
                        {indexed !== undefined && indexed < held && (
                            <Typography variant="caption" color="text.secondary">
                                {`${indexed.toLocaleString()} indexed`}
                            </Typography>
                        )}
                    </Box>
                );
            },
        },
        {
            id: 'lastSyncedAt',
            header: 'Last synced',
            size: 220,
            minSize: 160,
            maxSize: 260,
            Cell: ({ row }): JSX.Element => {
                const lastSyncedAt = row.original.object?.lastSyncedAt;
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">
                            {lastSyncedAt ? moment.utc(lastSyncedAt).format('YYYY-MM-DD HH:mm') + ' UTC' : 'Never'}
                        </Typography>
                        {/* Nothing is shown when no job is running: a source not syncing has nothing to report. */}
                        {row.original.object?.jobRunning && (
                            <Chip label="Sync running" size="small" color="info" variant="outlined" sx={{ height: 20, fontSize: '10px' }} />
                        )}
                    </Box>
                );
            },
        },
        // Sync and Rebuild are a super user's; anyone else would get a menu of things they cannot do.
        ...(authService.isSuperUser() ? [{
            id: 'actions',
            header: '',
            size: 30,
            enableColumnActions: false,
            enableSorting: false,
            enableResizing: false,
            Cell: ({ row }: {row: {original: ExtendedTreeViewItem}}): JSX.Element => (
                <IconButton
                    size="small"
                    onClick={(e): void => handleActionMenu(e, row.original)}
                >
                    <MoreVert fontSize="small" />
                </IconButton>
            ),
        }] : []),
    ];
};

const getFoundryEventColumns = (): MRT_ColumnDef<ExtendedTreeViewItem>[] => [
    {
        id: 'uniqueId',
        header: 'Primary ID',
        size: 200,
        minSize: 140,
        maxSize: 260,
        Cell: ({ row }): JSX.Element => (
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
                {row.original.object?.uniqueId || 'N/A'}
            </Typography>
        ),
    },
    {
        id: 'datetime',
        header: 'Date/Time',
        size: 150,
        minSize: 120,
        maxSize: 180,
        Cell: ({ row }): JSX.Element => {
            const datetime = row.original.object?.dateTime || row.original.object?.datetime;
            return (
                <Typography variant="body2">
                    {datetime ? moment.utc(datetime).format('YYYY-MM-DD HH:mm') + ' UTC' : 'N/A'}
                </Typography>
            );
        },
    },
    {
        id: 'source',
        header: 'Source',
        size: 100,
        minSize: 80,
        maxSize: 130,
        grow: true,
        Cell: ({ row }): JSX.Element => {
            const sourceName = row.original.object?.sourceName;
            return (
                <Box>
                    {sourceName && (
                        <Chip
                            label={sourceName}
                            size="small"
                            color="info"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '10px' }}
                        />
                    )}
                </Box>
            );
        },
    },
];

// Main function to get columns based on dataset type
export const getColumnsForDataset = (
    dataset: ExplorerDatasetType,
    options: ColumnConfigOptions
): MRT_ColumnDef<ExtendedTreeViewItem>[] => {
    switch (dataset) {
        case ResourceType.NEW_OCCURRENCE_REPORTS:
            return getOccurrenceReportColumns(options);
        case ResourceType.FOUNDRY_EVENTS:
            return getFoundryEventColumns();
        case ResourceType.SAFETY_RECOMMENDATIONS:
            return getSafetyRecommendationSourceColumns(options);
        case ResourceType.INTEGRATED_RISK_PICTURES:
            return getIRPColumns(options);
        case ResourceType.USERS:
            return getUserColumns(options);
        case ResourceType.COMPONENT_CATALOGUE:
            return getComponentCatalogueColumns(options);
        default:
            return getDefaultColumns(options);
    }
};

// Get column order based on dataset type
export const getColumnOrder = (dataset: ExplorerDatasetType): string[] => {
    switch (dataset) {
        case ResourceType.NEW_OCCURRENCE_REPORTS:
            return ['id', 'datetime', 'location', 'source', 'descriptors', 'actions'];
        case ResourceType.FOUNDRY_EVENTS:
            return ['uniqueId', 'datetime', 'source'];
        case ResourceType.SAFETY_RECOMMENDATIONS:
            return ['name', 'recommendationCount', 'lastSyncedAt', 'actions'];
        case ResourceType.INTEGRATED_RISK_PICTURES:
            return ['name', 'status', 'actions'];
        case ResourceType.USERS:
            return ['name', 'typeAndDetails', 'created', 'actions'];
        case ResourceType.COMPONENT_CATALOGUE:
            return ['name', 'releaseDate', 'workingArea', 'mappedSources', 'actions'];
        default:
            return ['name', 'typeAndDetails', 'actions'];
    }
};